import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Card, CircularProgress, Typography } from "@mui/material";

import { useAuth } from "context/userContext";

interface RouteAccessGuardProps {
    children: ReactNode;
    requiredMenuKey?: string;
    requiredRoles?: string[];
}

const ForbiddenState = () => (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" px={2}>
        <Card sx={{ maxWidth: 420, width: "100%", p: 4, textAlign: "center", borderRadius: 2 }}>
            <Box
                width={56}
                height={56}
                borderRadius="50%"
                bgcolor="error.light"
                color="error.main"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mx="auto"
                mb={2}>
                <LockOutlinedIcon />
            </Box>
            <Typography variant="h4" mb={1}>
                Access denied
            </Typography>
            <Typography variant="body2" color="text.secondary">
                You do not have permission to view this page.
            </Typography>
        </Card>
    </Box>
);

const RouteAccessGuard = ({ children, requiredMenuKey, requiredRoles }: RouteAccessGuardProps) => {
    const { authStatus, currentUser } = useAuth();

    if (authStatus === "loading") {
        return (
            <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (!currentUser) {
        return <Navigate to="/sign-in" replace />;
    }

    const userMenus = currentUser.menus ?? [];

    if (requiredMenuKey && !userMenus.includes(requiredMenuKey)) {
        return <ForbiddenState />;
    }

    if (requiredRoles && !requiredRoles.includes(currentUser.role)) {
        return <ForbiddenState />;
    }

    return <>{children}</>;
};

export default RouteAccessGuard;
